import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Translate, TranslateHtml } from "../../Translation/Translate";
import TranslationKeys, {
  entityTypesTranslations,
  getDocumentTypeTranslationKeys,
} from "../../../contexts/I18nContext/translationKeys";
import { useResolvedPath } from "react-router";
import {
  ProfilePermissionType,
  ProfilePermissionTypes,
} from "../../../data/dtos/profile-config.dto";
import { UserVerificationNeeded } from "../information/content-restricted-pages/UserVerificationNeeded";
import { DocumentsLoadingCard } from "./DocumentsLoadingCard";
import { InfoCard } from "../shared/InfoCard";
import { WhmFormHelperText, WhmGrid, WhmStack } from "@securitize/reactjs-whm";
import { DocumentUploader } from "./DocumentUploader";
import { ErrorPage } from "../../shared/ErrorPage";
import {
  useDeleteInvestorDocumentMutation,
  useGetDocumentsQuery,
  useUploadInvestorDocumentsMutation,
  useUploadInvestorDocumentTempMutation,
} from "../../../data/useProfile";
import ErrorSnackbar from "../../shared/ErrorSnackbar";
import { SnackbarContext } from "../../shared/Snackbar";
import { ResponseErrorBody } from "../../../data/types/responseError";
import {
  DocumentState,
  DocumentStatuses,
} from "./interfaces/documents-interfaces";
import {
  UploadInvestorDocumentsResponseErrorCode,
  UploadInvestorDocumentsResponseErrorCodes,
} from "../../../data/dtos/upload-investor-documents.dto";
import {
  UploadTempDocumentResponseErrorCode,
  UploadTempDocumentResponseErrorCodes,
} from "../../../data/dtos/upload-temp-document.dto";
import { I18nContext } from "../../../contexts/I18nContext";
import { SaveFooter } from "../shared/SaveFooter";
import translationKeys from "../../../contexts/I18nContext/translationKeys";
import { useQueryClient } from "@tanstack/react-query";
import { useGetOnboardingData } from "../../../data/usePortfolio";
import { KybSectionStatuses } from "../../../data/dtos/onboarding-status.dto";

export interface DocumentsProps {
  isPendingActionsLoading: boolean;
  permission: ProfilePermissionType;
}

function isDocumentFlagged(
  document: DocumentState,
  documentCommentsByDocumentId: Map<string, string>,
): boolean {
  return (
    !!document.documentId &&
    documentCommentsByDocumentId.has(document.documentId)
  );
}

export const Documents = ({
  isPendingActionsLoading,
  permission,
}: DocumentsProps) => {
  const { pathname: currentPath } = useResolvedPath("");
  const { showSnackbar } = useContext(SnackbarContext);
  const { getTranslation } = useContext(I18nContext);
  const queryClient = useQueryClient();
  const [documents, setDocuments] = useState<DocumentState[]>([]);
  const [isFakeSaving, setIsFakeSaving] = useState<boolean>(false);

  const documentsQuery = useGetDocumentsQuery();
  const onboardingQuery = useGetOnboardingData();
  const uploadInvestorDocumentTempMutation =
    useUploadInvestorDocumentTempMutation();
  const uploadInvestorDocumentsMutation = useUploadInvestorDocumentsMutation();
  const deleteInvestorDocumentsMutation = useDeleteInvestorDocumentMutation();

  const isDocumentsUpdatesRequired =
    onboardingQuery.data?.kyb.documents.status ===
    KybSectionStatuses.UPDATES_REQUIRED;

  const documentCommentsByDocumentId = useMemo(() => {
    const map = new Map<string, string>();
    for (const { documentId, comment } of onboardingQuery.data
      ?.requestedUpdatesGrouped?.documentComments ?? []) {
      map.set(documentId, comment);
    }
    return map;
  }, [onboardingQuery.data?.requestedUpdatesGrouped?.documentComments]);

  const updateDocumentState = useCallback(
    (documentState: DocumentState) => {
      setDocuments((prevDocuments) => {
        const updatedDocuments = [...prevDocuments];

        updatedDocuments[documentState.index] = documentState;

        return updatedDocuments;
      });
    },
    [setDocuments],
  );

  const isSaving =
    uploadInvestorDocumentTempMutation.isPending ||
    uploadInvestorDocumentsMutation.isPending ||
    deleteInvestorDocumentsMutation.isPending ||
    isFakeSaving;

  useEffect(() => {
    // Initialize documents state based on required documents
    if (documentsQuery.data?.requiredDocuments) {
      const initialDocuments: DocumentState[] =
        documentsQuery.data.requiredDocuments.map((document, index) => ({
          documentType: document.documentType,
          index,
          status: DocumentStatuses.PENDING,
        }));

      // Add uploaded documents to the initial state
      documentsQuery.data.uploadedDocuments.forEach((doc) => {
        const index = initialDocuments.findIndex(
          (d) => d.documentType === doc.docType,
        );
        if (index !== -1) {
          initialDocuments[index] = {
            documentId: doc.documentId,
            documentType: doc.docType,
            fileUrl: doc.url,
            fileName: doc.fileName,
            thumbnail: doc.thumbnail,
            index,
            status: DocumentStatuses.UPLOADED,
          };
        }
      });

      // Add a slot for each ad hoc document the operator requested that
      // isn't already part of this entity type's default required set.
      const additionalDocumentation =
        onboardingQuery.data?.requestedUpdatesGrouped
          ?.additionalDocumentation ?? [];
      additionalDocumentation.forEach((documentType) => {
        const alreadyPresent = initialDocuments.some(
          (d) => d.documentType === documentType,
        );
        if (!alreadyPresent) {
          initialDocuments.push({
            documentType,
            index: initialDocuments.length,
            status: DocumentStatuses.PENDING,
          });
        }
      });

      // A flagged document's type may no longer be part of this entity's
      // required set (e.g. requirements changed after it was uploaded), so
      // it wouldn't otherwise get a slot above. Surface it anyway so its
      // comment isn't silently lost.
      documentsQuery.data.uploadedDocuments.forEach((doc) => {
        const alreadyPresent = initialDocuments.some(
          (d) => d.documentId === doc.documentId,
        );
        if (
          !alreadyPresent &&
          documentCommentsByDocumentId.has(doc.documentId)
        ) {
          initialDocuments.push({
            documentId: doc.documentId,
            documentType: doc.docType,
            fileUrl: doc.url,
            fileName: doc.fileName,
            thumbnail: doc.thumbnail,
            index: initialDocuments.length,
            status: DocumentStatuses.UPLOADED,
          });
        }
      });

      // Flagged documents need the investor's attention first.
      initialDocuments.sort((a, b) => {
        const aFlagged = isDocumentFlagged(a, documentCommentsByDocumentId);
        const bFlagged = isDocumentFlagged(b, documentCommentsByDocumentId);
        return aFlagged === bFlagged ? 0 : aFlagged ? -1 : 1;
      });
      initialDocuments.forEach((document, index) => {
        document.index = index;
      });

      setDocuments(initialDocuments);
    }
  }, [
    documentsQuery.data,
    onboardingQuery.data?.requestedUpdatesGrouped?.additionalDocumentation,
    documentCommentsByDocumentId,
  ]);

  // Remove error state if all documents are uploaded
  const pendingUpload = useMemo(
    () =>
      !(
        documents.length > 0 &&
        documents.every((doc) => doc.status === DocumentStatuses.UPLOADED)
      ) ||
      documents.some((doc) =>
        isDocumentFlagged(doc, documentCommentsByDocumentId),
      ),
    [documents, documentCommentsByDocumentId],
  );

  const removeDocument = useCallback(
    async (documentType: string, documentId: string, index: number) => {
      await deleteInvestorDocumentsMutation.mutateAsync(
        { documentId },
        {
          onError: (error) => {
            showSnackbar({
              severity: "error",
              message: <ErrorSnackbar error={error} />,
            });
          },
          onSuccess: () => {
            // Invalidate onboarding data to refresh the onboarding card
            void queryClient.invalidateQueries({ queryKey: ["onboarding"] });
            // Invalidate pending actions to refresh the badge
            void queryClient.invalidateQueries({
              queryKey: ["pending-actions"],
            });

            updateDocumentState({
              documentType,
              index,
              status: DocumentStatuses.PENDING,
            });
            showSnackbar({
              severity: "info",
              message: (
                <Translate
                  translationKey={
                    translationKeys.PROFILE_DOCUMENTS_ENTITY_UPLOADER_REMOVE_SUCCESS
                  }
                  replacements={{
                    documentType: getTranslation(
                      getDocumentTypeTranslationKeys(documentType).name,
                    ),
                  }}
                />
              ),
            });
          },
        },
      );
    },
    [
      deleteInvestorDocumentsMutation,
      showSnackbar,
      updateDocumentState,
      getTranslation,
      queryClient,
    ],
  );

  const mapErrorTypeToErrorMessage = (
    errorType?: ResponseErrorBody["error"],
  ): string | undefined => {
    switch (
      errorType as
        | UploadTempDocumentResponseErrorCode
        | UploadInvestorDocumentsResponseErrorCode
    ) {
      case UploadTempDocumentResponseErrorCodes.INVALID_FILE_SIZE:
        return getTranslation(
          TranslationKeys.PROFILE_DOCUMENTS_ENTITY_UPLOADER_INVALID_SIZE_ERROR,
        );
      default:
        return getTranslation(
          TranslationKeys.PROFILE_DOCUMENTS_ENTITY_UPLOADER_GENERIC_ERROR,
        );
    }
  };

  const uploadTempFile = useCallback(
    async (file: File, documentType: string, index: number) => {
      const tempUploadResponse =
        await uploadInvestorDocumentTempMutation.mutateAsync(
          { file },
          {
            onError: (error) => {
              updateDocumentState({
                documentType,
                file,
                index,
                status: DocumentStatuses.ERROR,
                errorType: error.body?.error,
                errorMessage: mapErrorTypeToErrorMessage(error.body?.error),
              });

              showSnackbar({
                severity: "error",
                message: <ErrorSnackbar error={error} />,
              });
            },
          },
        );

      const uploadInvestorResponse =
        await uploadInvestorDocumentsMutation.mutateAsync(
          {
            documents: [
              {
                docType: documentType,
                docCategory: "entity",
                side: "front",
                fileName: tempUploadResponse.fileName,
                fileType: tempUploadResponse.fileType,
                url: tempUploadResponse.url,
              },
            ],
          },
          {
            onSuccess: () => {
              // Invalidate onboarding data to refresh the onboarding card
              void queryClient.invalidateQueries({ queryKey: ["onboarding"] });
              // Invalidate pending actions to refresh the badge
              void queryClient.invalidateQueries({
                queryKey: ["pending-actions"],
              });
            },
            onError: (error) => {
              updateDocumentState({
                documentType,
                file,
                index,
                status: DocumentStatuses.ERROR,
                errorMessage: mapErrorTypeToErrorMessage(
                  UploadInvestorDocumentsResponseErrorCodes.UNKNOWN_ERROR,
                ),
              });

              showSnackbar({
                severity: "error",
                message: <ErrorSnackbar error={error} />,
              });
            },
          },
        );

      const uploadedDocument = uploadInvestorResponse.data.find(
        (doc) => doc.docType === documentType,
      );

      updateDocumentState({
        documentType,
        file,
        fileName: uploadedDocument?.fileName,
        documentId: uploadedDocument?.documentId,
        index,
        status: DocumentStatuses.UPLOADED,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uploadInvestorDocumentTempMutation.mutate, showSnackbar],
  );

  const additionalDocumentationTypes = useMemo(
    () =>
      new Set(
        onboardingQuery.data?.requestedUpdatesGrouped
          ?.additionalDocumentation ?? [],
      ),
    [onboardingQuery.data?.requestedUpdatesGrouped?.additionalDocumentation],
  );

  const getDocumentErrorMessage = useCallback(
    (index: number): string | undefined => {
      if (index < 0 || index >= documents.length) {
        return undefined;
      }

      const document = documents[index];
      if (document.status === DocumentStatuses.ERROR) {
        return document.errorMessage;
      }
      if (
        document.status === DocumentStatuses.PENDING &&
        additionalDocumentationTypes.has(document.documentType)
      ) {
        return getTranslation(
          TranslationKeys.PROFILE_DOCUMENTS_ENTITY_ADDITIONAL_DOCUMENT_REQUIRED,
        );
      }
      return undefined;
    },
    [documents, additionalDocumentationTypes, getTranslation],
  );

  if (permission === ProfilePermissionTypes.USER_VERIFICATION_NOT_SUBMITTED) {
    return (
      <UserVerificationNeeded
        isVerificationInReview={false}
        currentPath={currentPath}
      />
    );
  }

  if (permission === ProfilePermissionTypes.USER_VERIFICATION_PENDING) {
    return (
      <UserVerificationNeeded
        isVerificationInReview
        currentPath={currentPath}
      />
    );
  }

  // Main UI
  if (isPendingActionsLoading || documentsQuery.isPending)
    return <DocumentsLoadingCard />;

  if (documentsQuery.error)
    return (
      <ErrorPage
        errorCode={documentsQuery.error.correlationId}
        onRefresh={documentsQuery.refetch}
      />
    );

  return (
    <WhmStack sx={{ gap: "20px" }}>
      <InfoCard
        title={
          documentsQuery.data.entityType ? (
            <Translate
              translationKey={
                TranslationKeys.PROFILE_DOCUMENTS_ENTITY_CARD_TITLE
              }
              replacements={{
                entityType: getTranslation(
                  entityTypesTranslations[documentsQuery.data.entityType],
                ),
              }}
            />
          ) : undefined
        }
        subtitle={
          <Translate
            translationKey={
              TranslationKeys.PROFILE_DOCUMENTS_ENTITY_CARD_SUBTITLE
            }
          />
        }
        disclaimer={
          <Translate
            translationKey={
              TranslationKeys.PROFILE_DOCUMENTS_ENTITY_CARD_DISCLAIMER
            }
          />
        }
        footer={
          permission === ProfilePermissionTypes.READ_ONLY && (
            <TranslateHtml
              translationKey={
                TranslationKeys.PROFILE_DOCUMENTS_ENTITY_READ_ONLY_UPDATE_FOOTER
              }
            />
          )
        }
      >
        {pendingUpload && permission !== ProfilePermissionTypes.READ_ONLY && (
          <WhmFormHelperText error sx={{ marginBottom: "16px !important" }}>
            <Translate
              translationKey={
                TranslationKeys.PROFILE_DOCUMENTS_ENTITY_MISSING_ERROR
              }
            />
          </WhmFormHelperText>
        )}

        <WhmGrid container spacing={5}>
          {documents.map((document, index) => (
            <WhmGrid item xs={12} lg={6} key={index}>
              <DocumentUploader
                documentType={document.documentType}
                fileName={document.fileName}
                disabled={
                  permission === ProfilePermissionTypes.READ_ONLY ||
                  (isDocumentsUpdatesRequired &&
                    document.status === DocumentStatuses.UPLOADED &&
                    !isDocumentFlagged(document, documentCommentsByDocumentId))
                }
                fileUrl={document.fileUrl}
                onUpload={(file: File): Promise<void> => {
                  return uploadTempFile(file, document.documentType, index);
                }}
                onDelete={async (): Promise<void> => {
                  return removeDocument(
                    document.documentType,
                    document.documentId!,
                    index,
                  );
                }}
                errorMessage={getDocumentErrorMessage(index)}
                flaggedComment={
                  document.documentId
                    ? documentCommentsByDocumentId.get(document.documentId)
                    : undefined
                }
                title={
                  <Translate
                    translationKey={
                      getDocumentTypeTranslationKeys(document.documentType).name
                    }
                  />
                }
                subtitle={
                  <Translate
                    translationKey={
                      getDocumentTypeTranslationKeys(document.documentType)
                        .description
                    }
                  />
                }
              />
            </WhmGrid>
          ))}
        </WhmGrid>
      </InfoCard>
      {permission !== ProfilePermissionTypes.READ_ONLY && (
        <SaveFooter
          isLoading={isSaving}
          onSave={() => {
            setIsFakeSaving(true);
            setTimeout(() => {
              setIsFakeSaving(false);
            }, 2000);
          }}
          saveButtonText={
            <Translate
              translationKey={
                isSaving
                  ? TranslationKeys.PROFILE_DOCUMENTS_ENTITY_CTA_LOADING
                  : TranslationKeys.PROFILE_DOCUMENTS_ENTITY_CTA
              }
            />
          }
          footer={
            <TranslateHtml
              translationKey={TranslationKeys.PROFILE_DOCUMENTS_ENTITY_FOOTER}
            />
          }
        />
      )}
    </WhmStack>
  );
};
